import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCurrentTenant,
  listEstablishments,
  me,
  setCurrentEstablishment,
  type AdminMe,
} from "../lib/api";
import { useCurrentEstablishment } from "../lib/scope";

/**
 * Sélecteur d'Établissement courant (Sprint M).
 *
 * - Affiché sous le TenantSwitcher (Client).
 * - Filtré par Tenant courant : si le Client = EPMO, seuls les Etabs Orsay
 *   et Orangerie apparaissent. Si Client = Tous (SUPER_ADMIN), tous les
 *   Etabs sont listés.
 * - "Tous" reste disponible pour SUPER_ADMIN.
 * - La sélection ne déclenche PAS de header HTTP : c'est juste un défaut UI
 *   pour pré-remplir les filtres "?establishment=" des pages Content.
 */
export function EstablishmentSwitcher() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const meQuery = useQuery<AdminMe>({ queryKey: ["admin", "me"], queryFn: me });
  const establishmentsQuery = useQuery({
    queryKey: ["admin", "establishments"],
    queryFn: listEstablishments,
    enabled: meQuery.isSuccess,
  });

  const currentTenant = getCurrentTenant();
  const current = useCurrentEstablishment();
  const isSuperAdmin = meQuery.data?.role === "SUPER_ADMIN";

  // Visible : filtre par tenant courant (sauf SUPER_ADMIN vue globale)
  const visibleEstablishments =
    establishmentsQuery.data?.filter((e) =>
      currentTenant ? e.tenant.slug === currentTenant : true,
    ) ?? [];

  const currentEst = visibleEstablishments.find((e) => e.slug === current) ?? null;

  // Si le tenant change et que l'établissement courant n'est plus visible, on
  // reset l'établissement.
  useEffect(() => {
    // Tant que la liste n'est pas chargee elle est vide : sans ce garde-fou,
    // tout rechargement de page effacait le musee choisi.
    if (!establishmentsQuery.isSuccess) return;
    if (current && !visibleEstablishments.find((e) => e.slug === current)) {
      setCurrentEstablishment(null);
      void qc.invalidateQueries({ queryKey: ["admin"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant, visibleEstablishments.length, establishmentsQuery.isSuccess]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const select = (slug: string | null) => {
    setCurrentEstablishment(slug);
    setOpen(false);
    void qc.invalidateQueries({ queryKey: ["admin"] });
  };

  let buttonLabel: string;
  let buttonHint: string;
  if (currentEst) {
    buttonLabel = currentEst.name;
    buttonHint = currentEst.shortCode ?? "";
  } else if (isSuperAdmin || !currentTenant) {
    buttonLabel = "Tous les établissements";
    buttonHint = "—";
  } else if (visibleEstablishments.length === 0) {
    buttonLabel = "Aucun pour ce client";
    buttonHint = "—";
  } else {
    buttonLabel = "Tous les établissements";
    buttonHint = "—";
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={visibleEstablishments.length === 0}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-md border border-[color:var(--color-alto-border)] bg-white hover:bg-[color:var(--color-alto-bg)] text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-[color:var(--color-alto-muted)]">
            Établissement
          </p>
          <p className="text-sm font-medium truncate">{buttonLabel}</p>
        </div>
        <span className="text-xs font-mono text-[color:var(--color-alto-muted)] shrink-0">
          {buttonHint}
        </span>
        <svg
          aria-hidden
          width="12"
          height="12"
          viewBox="0 0 12 12"
          className={"shrink-0 transition-transform " + (open ? "rotate-180" : "")}
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute top-full mt-1 left-0 right-0 z-20 max-h-72 overflow-y-auto bg-white border border-[color:var(--color-alto-border)] rounded-md shadow-lg py-1"
        >
          <li>
            <button
              type="button"
              onClick={() => select(null)}
              className={
                "w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--color-alto-bg)] " +
                (current === null ? "bg-[color:var(--color-alto-bg)] font-medium" : "")
              }
            >
              <span className="block">Tous les établissements</span>
              <span className="block text-xs text-[color:var(--color-alto-muted)]">
                {currentTenant ? `dans le client courant` : `vue globale`}
              </span>
            </button>
          </li>
          {visibleEstablishments.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => select(e.slug)}
                className={
                  "w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--color-alto-bg)] " +
                  (current === e.slug ? "bg-[color:var(--color-alto-bg)] font-medium" : "")
                }
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate">{e.name}</span>
                  {e.shortCode && (
                    <span className="text-xs font-mono text-[color:var(--color-alto-muted)] shrink-0">
                      {e.shortCode}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
